import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
}

interface ToastContextType {
  toast: (message: string, type?: Toast['type']) => void;
}

const ToastContext = createContext<ToastContextType>({ toast: () => {} });

export function useToast() {
  return useContext(ToastContext);
}

let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((message: string, type: Toast['type'] = 'info') => {
    const id = nextId++;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const colors: Record<Toast['type'], { bg: string; border: string; text: string; icon: string }> = {
    success: { bg: 'var(--toast-success-bg, #d1fae5)', border: '#10b981', text: 'var(--toast-success-text, #065f46)', icon: '\u2713' },
    error: { bg: 'var(--toast-error-bg, #fee2e2)', border: '#ef4444', text: 'var(--toast-error-text, #991b1b)', icon: '\u2717' },
    warning: { bg: 'var(--toast-warning-bg, #fef3c7)', border: '#f59e0b', text: 'var(--toast-warning-text, #92400e)', icon: '\u26A0' },
    info: { bg: 'var(--toast-info-bg, #dbeafe)', border: '#3b82f6', text: 'var(--toast-info-text, #1e40af)', icon: '\u2139' },
  };

  return (
    <ToastContext.Provider value={{ toast: addToast }}>
      {children}
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        maxWidth: '400px',
      }}>
        {toasts.map(t => {
          const c = colors[t.type];
          return (
            <div
              key={t.id}
              style={{
                padding: '12px 16px',
                backgroundColor: c.bg,
                borderLeft: `4px solid ${c.border}`,
                borderRadius: '6px',
                color: c.text,
                fontSize: '14px',
                fontWeight: 500,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
                animation: 'toast-slide-in 0.3s ease-out',
              }}
              onClick={() => removeToast(t.id)}
            >
              <span style={{ fontSize: '16px', flexShrink: 0 }}>{c.icon}</span>
              <span style={{ flex: 1 }}>{t.message}</span>
              <span style={{ opacity: 0.5, fontSize: '18px', lineHeight: 1, flexShrink: 0 }}>&times;</span>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
